import React from "react";
import { useQueryClient } from "@tanstack/react-query";

import { apiErrorMessage } from "../../../assets/utils/lib/error";
import { Constants } from "../../../assets/utils/lib/constants";
import { GRID_ACTION_TYPES, GridActionType } from "../../../assets/types";
import { isGridActionType } from "../../../assets/utils/helpers";
import { LoadingIndicator } from "../../../assets/vfx";
import { MessageSeverity } from "../../../assets/utils/lib/messageSeverity";
import { useSnackbar } from "../../../assets/vfx/snackbar/vfxSnackbarContext";

import { StateDto } from "../../../services/dto";
import { StateForm } from "./StateForm";
import { StatePage } from "./StatePage";
import { StateService } from "./stateService";
import { StateView } from "./StateView";
import { useActionFromQuery } from "../../../hooks/useActionFromQuery";

export const StateContainer = () => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [data, setData] = React.useState<StateDto>();

  const isEditMode = !!(data?.id && data.id !== Constants.emptyGuid);
  const closeDrawer = (): void => {
      setDrawerType(GRID_ACTION_TYPES.HIDDEN);
      clearSearchAction();
  }

  const [drawerType, setDrawerType] = React.useState<GridActionType>(
    GRID_ACTION_TYPES.HIDDEN
  );

  const stateQuery = StateService.getStateQuery(
    data?.id ?? Constants.emptyGuid
  );

  const regionLookupQuery = StateService.regionLookupQuery();

  const handleEdit = (): void => {
    setData(stateQuery.data);
    setDrawerType(GRID_ACTION_TYPES.EDIT);
    setSearchAction(GRID_ACTION_TYPES.EDIT, stateQuery.data?.id);
  };

  const handleCustomEvent = (actionType: string, dataId: string): void => {
    if (isGridActionType(actionType)) {
      setDrawerType(actionType);
      setData({ id: dataId } as StateDto);
      setSearchAction(actionType, dataId);
    }
  };
  const { setSearchAction, clearSearchAction } = useActionFromQuery(handleCustomEvent);
  const { mutateAsync: saveState } = StateService.save();
  const { mutate: deleteMutate } = StateService.deleteState();

  const refreshGrid = (): void => {
    isEditMode && stateQuery.refetch();
    queryClient.invalidateQueries({ queryKey: ["stateGridQuery"] });
    closeDrawer();
  };

  const handleApiResponse = (response: any, successMessage: string): void => {
    const errorMsg = apiErrorMessage(response.errorMessages);
    if (errorMsg) {
      showSnackbar(errorMsg, MessageSeverity.Error);
    } else {
      showSnackbar(successMessage, MessageSeverity.Success);
      refreshGrid();
    }
  };

  const handleSubmit = async (values: StateDto) => {
    values.id = isEditMode ? data!.id : Constants.emptyGuid;
    const message = isEditMode ? "State updated" : "State saved";
    return await saveState(values, {
      onSuccess: (response) => handleApiResponse(response, message),
      onError(error: any) {
        const errors = error.response.data.errors;
        if (errors) {
          showSnackbar("Failed to save", MessageSeverity.Error);
        }
      },
    });
  };

  const handleDelete = (): void => {
    deleteMutate(data?.id ?? Constants.emptyGuid, {
      onSuccess: (response) => handleApiResponse(response, "State deleted"),
    });
  };

  const renderDrawerContent = (): React.JSX.Element | null => {
    switch (drawerType) {
      case GRID_ACTION_TYPES.NEW:
        return (
          <StateForm
            show={true}
            onHide={closeDrawer}
            onSave={handleSubmit}
            regionLookup={regionLookupQuery?.data}
          />
        );

      case GRID_ACTION_TYPES.EDIT:
        return (
          <StateForm
            data={stateQuery.data}
            loading={stateQuery.isLoading}
            show={!!stateQuery.data}
            onHide={closeDrawer}
            onSave={handleSubmit}
            regionLookup={regionLookupQuery?.data}
          />
        );

      case GRID_ACTION_TYPES.VIEW:
      case GRID_ACTION_TYPES.DOUBLE_CLICK:
        return (
          <StateView
            data={stateQuery?.data}
            show={!!stateQuery.data}
            onHide={closeDrawer}
            onDelete={handleDelete}
            onEdit={handleEdit}
            regionLookup={regionLookupQuery?.data}
          />
        );

      default:
        return null;
    }
  };

  if (regionLookupQuery.isLoading) {
    return <LoadingIndicator isFullPage />;
  }

  return (
    <>
      <StatePage
        regionLookupValues={regionLookupQuery?.data}
        onCustomEvent={handleCustomEvent}
      />
      {renderDrawerContent()}
    </>
  );
};
